import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Clock, Lock, PlayCircle, Save } from 'lucide-react';
import { api } from '../api/client';
import { useAuthStore } from '../store/auth';
import { toast } from '../lib/toast';
import ScoreDropAlertForm from './ScoreDropAlertForm';
import BotActivityAlertForm from './BotActivityAlertForm';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// True iff the user's entitlement set includes autoPilot.active (lazy expiry
// is applied the same way ownedEntitlements does it on the backend).
function hasAutoPilotFeature(user) {
  const ap = user?.entitlements?.autoPilot;
  if (!ap?.active) return false;
  if (!ap.expiresAt) return true;
  return new Date(ap.expiresAt).getTime() > Date.now();
}

export default function AutoPilotScheduleForm({ domain }) {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const locked = !hasAutoPilotFeature(user);

  // Don't even query the API when locked — saves a 403 round-trip every
  // domain switch and the form is hidden anyway.
  const scheduleQuery = useQuery({
    queryKey: ['autopilot-schedule', domain._id],
    queryFn: () =>
      api.get(`/api/domains/${domain._id}/autopilot/schedule`).then((r) => r.data.schedule),
    enabled: !locked,
  });

  const [draft, setDraft] = useState(null);
  // Sync the local draft to whatever the server says on first load / domain switch.
  useEffect(() => {
    if (scheduleQuery.data) {
      setDraft({
        isActive: !!scheduleQuery.data.isActive,
        autoRewriteEnabled: !!scheduleQuery.data.autoRewriteEnabled,
        cadence: scheduleQuery.data.cadence || 'weekly',
        hourUtc: scheduleQuery.data.hourUtc ?? 12,
        dayOfWeek: scheduleQuery.data.dayOfWeek ?? 1,
        dayOfMonth: scheduleQuery.data.dayOfMonth ?? 1,
      });
    }
  }, [scheduleQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (body) =>
      api.put(`/api/domains/${domain._id}/autopilot/schedule`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['autopilot-schedule', domain._id] });
      toast.success('AutoPilot schedule saved');
    },
    onError: (err) => toast.error(err, 'Could not save schedule'),
  });

  const runNowMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/domains/${domain._id}/autopilot/schedule/run-now`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Audit enqueued — it will run within ~1 minute');
    },
    onError: (err) => toast.error(err, 'Could not enqueue audit'),
  });

  if (locked) {
    return (
      <div className="card border-dashed border-2 border-gray-200 bg-gray-50/60">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Lock className="w-4 h-4" />
            </span>
            AutoPilot scheduled audits
            <span className="pill bg-gray-100 text-gray-500">Locked</span>
          </div>
        </div>
        <p className="text-sm text-ink-500">
          AutoPilot runs your audits automatically on the cadence you pick, then emails you the
          score change and what's newly broken. Upgrade to AutoPilot to enable it.
        </p>
      </div>
    );
  }

  if (scheduleQuery.isLoading || !draft) {
    return (
      <div className="card">
        <div className="section-title mb-2">
          <span className="section-icon">
            <Calendar className="w-4 h-4" />
          </span>
          AutoPilot scheduled audits
        </div>
        <p className="text-sm text-ink-400">Loading…</p>
      </div>
    );
  }

  const nextRunAtLocal = scheduleQuery.data?.nextRunAt
    ? new Date(scheduleQuery.data.nextRunAt).toLocaleString()
    : null;
  const lastRunAtLocal = scheduleQuery.data?.lastRunAt
    ? new Date(scheduleQuery.data.lastRunAt).toLocaleString()
    : null;

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Calendar className="w-4 h-4" />
          </span>
          AutoPilot scheduled audits
          {draft.isActive ? (
            <span className="pill bg-emerald-50 text-emerald-700">On</span>
          ) : (
            <span className="pill bg-gray-100 text-gray-500">Off</span>
          )}
        </div>
        <button
          type="button"
          className="btn-ghost text-xs"
          disabled={runNowMutation.isPending}
          onClick={() => runNowMutation.mutate()}
          title="Enqueue an audit for this domain right now"
        >
          <PlayCircle className="w-3.5 h-3.5" />
          {runNowMutation.isPending ? 'Enqueuing…' : 'Run now'}
        </button>
      </div>

      <p className="text-sm text-ink-500 mb-4">
        Pick a cadence — RankOnAI will run a fresh audit on this domain at that schedule,
        then email you the result.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">Active</span>
          <button
            type="button"
            className={`w-full rounded-lg px-3 py-2 text-left transition ${
              draft.isActive
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                : 'bg-gray-50 border border-gray-200 text-ink-700'
            }`}
            onClick={() => setDraft({ ...draft, isActive: !draft.isActive })}
          >
            <span className="inline-flex items-center gap-2">
              <span
                className={`inline-block w-2 h-2 rounded-full ${
                  draft.isActive ? 'bg-emerald-500' : 'bg-gray-300'
                }`}
              />
              {draft.isActive ? 'Scheduled audits enabled' : 'Scheduled audits paused'}
            </span>
          </button>
        </label>

        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
            Auto-rewrite slipped pages
          </span>
          <button
            type="button"
            className={`w-full rounded-lg px-3 py-2 text-left transition ${
              draft.autoRewriteEnabled
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                : 'bg-gray-50 border border-gray-200 text-ink-700'
            }`}
            onClick={() => setDraft({ ...draft, autoRewriteEnabled: !draft.autoRewriteEnabled })}
            title="Generate rewrites for pages whose score slips — queued for your approval, never auto-published"
          >
            <span className="inline-flex items-center gap-2">
              <span
                className={`inline-block w-2 h-2 rounded-full ${
                  draft.autoRewriteEnabled ? 'bg-emerald-500' : 'bg-gray-300'
                }`}
              />
              {draft.autoRewriteEnabled ? 'Queue rewrites to approve' : 'Auto-rewrite off'}
            </span>
          </button>
        </label>

        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
            Cadence
          </span>
          <select
            className="input"
            value={draft.cadence}
            onChange={(e) => setDraft({ ...draft, cadence: e.target.value })}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </label>

        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
            Hour (UTC)
          </span>
          <select
            className="input"
            value={draft.hourUtc}
            onChange={(e) => setDraft({ ...draft, hourUtc: Number(e.target.value) })}
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, '0')}:00 UTC
              </option>
            ))}
          </select>
        </label>

        {draft.cadence === 'weekly' && (
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
              Day of week
            </span>
            <select
              className="input"
              value={draft.dayOfWeek}
              onChange={(e) => setDraft({ ...draft, dayOfWeek: Number(e.target.value) })}
            >
              {DAYS_OF_WEEK.map((d, i) => (
                <option key={i} value={i}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        )}

        {draft.cadence === 'monthly' && (
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
              Day of month (1-28)
            </span>
            <select
              className="input"
              value={draft.dayOfMonth}
              onChange={(e) => setDraft({ ...draft, dayOfMonth: Number(e.target.value) })}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-ink-500 flex items-center gap-4">
          {nextRunAtLocal && (
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> Next: {nextRunAtLocal}
            </span>
          )}
          {lastRunAtLocal && (
            <span className="text-ink-400">Last: {lastRunAtLocal}</span>
          )}
        </div>
        <button
          type="button"
          className="btn-primary text-sm"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate(draft)}
        >
          <Save className="w-4 h-4" />
          {saveMutation.isPending ? 'Saving…' : 'Save schedule'}
        </button>
      </div>

      <ScoreDropAlertForm domain={domain} />
      <BotActivityAlertForm domain={domain} />
    </div>
  );
}
